import type { Link, SpanContext as OtelSpanContext, Tracer } from '@opentelemetry/api';
import {
    context,
    defaultTextMapSetter,
    propagation,
    ROOT_CONTEXT,
    trace,
    TraceFlags,
} from '@opentelemetry/api';
import { createContext, useCallback, useContext, useEffect, useState } from 'react'; //  Dispatch, SetStateAction,

import { FORGE_REQUEST_TRANSACTION } from './metrics.constants';
import type { SpanContextWrapper, SpanOrNull, SpanSetter } from './metrics.types';
import { flushWebTracedSpan, getParentSpan, getWebTracer } from './trace';

/**
 * Type for props to initialize a Span on an already initialized TraceProvider
 */
export type StartSpanArgs = Parameters<Tracer['startSpan']> | null;
export type StartSpanContext = {
    tracerName: string;
    spanArgs: StartSpanArgs;
};

let sessionContext = context.active();

/**
 * Initialize the Context provider with a span for the Consumer to useSpan
 * A High-order-component like Router or ContentAreaWrapper initializing this
 * adds a Context.Provider that wraps _children_ view components, such that the
 * descendants can make use of the Context.Consumer hooks to add & manage Spans.
 * Throws @error when the TraceProvider has not been initialized
 * @param param0 initialize a parentSpan as the ROOT_CONTEXT
 * @returns [
 *  spanner: SpanOrNull the initialized Span that can be ended or chained, via the
 *  startNextOrEndSpan: SpanSetter for managing the chaining or ending of a transaction.
 * ]
 */
export function useSpanContext({ tracerName, spanArgs }: StartSpanContext): SpanContextWrapper {
    const tracer = getWebTracer(tracerName);
    if (!tracer) {
        const error = new Error('useSpan depends on an OtelTracer');
        throw error;
    }
    const parentSpan = getParentSpan();
    const traceFlags = TraceFlags.SAMPLED;

    // A pigeon of sorts to carry the SpanContext across the Provider lifecycle
    const carrier: any = {};
    const spanContext: OtelSpanContext = {
        spanId: '',
        traceFlags,
        traceId: '',
    };
    const spanOptions = { ...FORGE_REQUEST_TRANSACTION };
    const currContext = context.active();
    if (spanArgs?.length === 3) {
        Object.assign(currContext, spanArgs.pop());
    }
    if (parentSpan) {
        propagation.extract(context.active(), carrier);
        Object.assign(currContext, parentSpan.spanContext());
        Object.assign(spanContext, parentSpan);
    }
    if (spanArgs?.length === 2) {
        Object.assign(spanOptions, spanArgs.pop());
    }
    const link: Link = {
        context: spanContext,
    };
    Object.assign(spanOptions, { links: [link] });

    const span = spanArgs
        ? (tracer as unknown as Tracer).startSpan(spanArgs[0], spanOptions, currContext)
        : null;
    if (span) {
        trace.setSpan(ROOT_CONTEXT, span);
        propagation.inject(sessionContext, {}, defaultTextMapSetter);
    }

    const [spanner, setSpanner] = useState<SpanOrNull>(span);

    const startNextOrEndSpan: SpanSetter = useCallback(
        (newSpan: SpanOrNull): void => {
            // Extract the context from the carrier. This propagates the parent Span to the newSpan
            propagation.extract(context.active(), carrier);
            // Create an Outgoing Link on the existing Span to the newSpan
            if (newSpan && spanner) {
                const ctx = newSpan.spanContext();
                spanner.addLink({ context: ctx });
            }
            if (spanner) {
                // Finalize the Span
                spanner.end();
                flushWebTracedSpan();
            }
            if (newSpan) {
                trace.setSpan(ROOT_CONTEXT, newSpan);
                // Propagate the context to the newSpan
                sessionContext = context.active();
                propagation.inject(sessionContext, carrier, defaultTextMapSetter);
            }
            setSpanner(newSpan);
        },
        [spanner, setSpanner],
    );
    useEffect(() => {
        function onUnmount() {
            if (spanner) {
                // Finalize the Span
                spanner.end();
            }
            flushWebTracedSpan();
        }
        return () => onUnmount();
    }, []);
    return [spanner, startNextOrEndSpan];
}

export const SpanContext = createContext<SpanContextWrapper | null>(null);
/**
 * Hook for View components to access the current activeSpan & manage it
 * Throws @error when without the Provider parent implementation (via useSpanContext)
 * or when the TraceProvider has not been initialized
 * @param tracerName Gives the Span a name that results in captured Transactions
 * @returns [
 *  activeSpan: SpanOrNull the initialized Span that can be ended or chained, via the
 *  setactiveSpan: SpanSetter for managing the chaining or ending of a transaction.
 * ]
 */
export function useSpan(tracerName: string): SpanContextWrapper {
    const spanContext = useContext(SpanContext);
    if (!spanContext) {
        const error = new Error('useSpan must be used within an SpanContext provider');
        throw error;
    }

    const tracer = getWebTracer(tracerName);
    if (!tracer) {
        const error = new Error('useSpan depends on an OtelTracer');
        throw error;
    }

    const [activeSpan, setActiveSpan] = spanContext;

    useEffect(() => {
        function onUnmount() {
            if (activeSpan) {
                // Finalize the Span
                activeSpan.end();
            }
            flushWebTracedSpan();
        }
        return () => onUnmount();
    }, [activeSpan]);

    return [activeSpan, setActiveSpan];
}
